import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Tag, Typography, theme } from 'antd';
import { StatCell } from '@/components/common/show/StatCell';
import type { PublisherDetailOut } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { ShowLink } from '@/components/common/ShowLink';
import { CoverThumb } from '@/components/common/show/CoverThumb';
import { PUBLISHER_SHOW_TABS } from '../constants';
import type { PublisherShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<PublisherShowTab, string> = {
  general: 'base.sections.basic_info',
  publications: 'user_catalog.common.tabs.publications',
  collection: 'base.sections.collection_info',
  search: 'user_catalog.common.tabs.search',
};

interface PublisherShowHeaderProps {
  record: PublisherDetailOut;
  activeTab: PublisherShowTab;
  onTabChange: (key: string) => void;
}

export const PublisherShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: PublisherShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const isImprint = record.parent_id != null;

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <CoverThumb url={record.cover_url} size={128} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.name}
            </Title>
            <Tag color={isImprint ? 'blue' : undefined}>
              {isImprint
                ? t('user_catalog.publishers.show.kind_imprint')
                : t('user_catalog.publishers.show.kind_publisher')}
            </Tag>
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            {isImprint ? (
              <StatCell label={t('user_catalog.publishers.fields.parent')}>
                {record.parent_id != null && (
                  <ShowLink
                    resource="user_catalog.publishers"
                    id={record.parent_id}
                  >
                    {record.parent?.name ?? record.parent_id}
                  </ShowLink>
                )}
              </StatCell>
            ) : (
              <StatCell label={t('user_catalog.publishers.fields.imprints')}>
                {record.imprints?.length ?? 0}
              </StatCell>
            )}
            <StatCell label={t('base.fields.mod_status')}>
              <ModStatusTag value={record.mod_status} />
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={PUBLISHER_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
